/**
 * Dialog de cobro: selecciona serie y método de pago, calcula vuelto y emite.
 *
 * Flujo de emisión (3 calls al backend, secuencial):
 *   1. POST /sales/draft
 *   2. Por cada línea del carrito: POST /sales/{id}/items
 *   3. POST /sales/{id}/issue con la serie y los pagos
 */

import { useQueryClient } from "@tanstack/react-query";
import { CreditCard, Loader2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CustomerFormDialog } from "@/components/admin/CustomerFormDialog.tsx";
import { CustomerSelector } from "@/components/CustomerSelector.tsx";
import { ApiError, api } from "@/lib/api.ts";
import { useBranchSeries } from "@/queries/branches.ts";
import { cashSessionKeys } from "@/queries/cashSessions.ts";
import { useCartStore, computeTotals } from "@/stores/cart.ts";
import { useTerminalStore } from "@/stores/terminal.ts";
import { formatPEN, toNumber } from "@/lib/utils.ts";
import {
  CustomerDocType,
  type CustomerOut,
  type DocumentSeriesOut,
  type PaymentInput,
  PaymentMethod,
  type SaleDetailOut,
  type SaleOut,
} from "@/types/api.ts";

interface Props {
  branchId: string;
  onClose: () => void;
}

const METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.CASH]: "Efectivo",
  [PaymentMethod.YAPE]: "Yape",
  [PaymentMethod.PLIN]: "Plin",
  [PaymentMethod.CARD_DEBIT]: "Débito",
  [PaymentMethod.CARD_CREDIT]: "Crédito",
  [PaymentMethod.TRANSFER]: "Transferencia",
  [PaymentMethod.OTHER]: "Otro",
};

/** Devuelve la id de la serie default del terminal para un doctype dado. */
function terminalDefaultFor(
  terminal:
    | {
        default_boleta_series_id?: string | null;
        default_factura_series_id?: string | null;
        default_nota_venta_series_id?: string | null;
      }
    | null
    | undefined,
  docType: "boleta" | "factura" | "nota_venta",
): string | null {
  if (!terminal) return null;
  if (docType === "boleta") return terminal.default_boleta_series_id ?? null;
  if (docType === "factura") return terminal.default_factura_series_id ?? null;
  return terminal.default_nota_venta_series_id ?? null;
}

export function PaymentDialog({ branchId, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lines = useCartStore((s) => s.lines);
  const clearCart = useCartStore((s) => s.clear);
  const totals = useMemo(() => computeTotals(lines), [lines]);

  const series = useBranchSeries(branchId);
  const terminal = useTerminalStore((s) => s.terminal);
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [amountStr, setAmountStr] = useState(totals.total.toFixed(2));
  const [reference, setReference] = useState("");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isEmitting, setIsEmitting] = useState(false);
  const [customer, setCustomer] = useState<CustomerOut | null>(null);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerFormSeed, setCustomerFormSeed] = useState("");

  // Auto-elegir serie al cargar:
  //  1) default del terminal pareado para boleta (boleta es la más usada y la
  //     convención en Perú de "venta minorista") si está configurada.
  //  2) primera serie boleta disponible.
  //  3) primera serie cualquiera.
  if (!seriesId && series.data && series.data.length > 0) {
    const defaultBoletaId = terminalDefaultFor(terminal, "boleta");
    const fromTerminal =
      defaultBoletaId != null ? series.data.find((s) => s.id === defaultBoletaId) : null;
    const fallback =
      fromTerminal ?? series.data.find((s) => s.document_type === "boleta") ?? series.data[0];
    setSeriesId(fallback.id);
  }

  const selectedSeries = useMemo(
    () => series.data?.find((s) => s.id === seriesId) ?? null,
    [series.data, seriesId],
  );
  const docType = selectedSeries?.document_type ?? null;
  // Reglas SUNAT: factura requiere cliente con RUC; boleta acepta opcional;
  // nota_venta no es comprobante fiscal y no necesita cliente.
  const customerMode: "required-ruc" | "optional" | "hidden" =
    docType === "factura" ? "required-ruc" : docType === "boleta" ? "optional" : "hidden";

  const amountReceived = toNumber(amountStr);
  const remaining = Math.max(0, totals.total - amountReceived);
  const change = method === PaymentMethod.CASH ? Math.max(0, amountReceived - totals.total) : 0;
  const customerOk =
    customerMode === "hidden" ||
    customerMode === "optional" ||
    (customerMode === "required-ruc" && customer?.doc_type === CustomerDocType.RUC);
  const canEmit =
    seriesId !== null &&
    amountReceived >= totals.total &&
    lines.length > 0 &&
    customerOk &&
    !isEmitting;

  const handleEmit = async () => {
    if (!seriesId) return;
    setSubmissionError(null);
    setIsEmitting(true);
    try {
      // 1. Crear draft (con customer_id si ya está elegido)
      const draft = await api.post<SaleOut>("/sales/draft", {
        branch_id: branchId,
        customer_id: customer?.id ?? null,
      });

      // 2. Agregar items (secuencial; el backend recalcula totales en cada add)
      for (const line of lines) {
        await api.post(`/sales/${draft.id}/items`, {
          product_id: line.productId,
          quantity: line.quantity.toString(),
        });
      }

      // 3. Emitir (customer_id explícito por si cambió desde el draft)
      const payment: PaymentInput = {
        method,
        amount: amountReceived.toFixed(2),
        change_given: change.toFixed(2),
        reference: reference || null,
      };
      const issued = await api.post<SaleDetailOut>(`/sales/${draft.id}/issue`, {
        series_id: seriesId,
        customer_id: customer?.id ?? null,
        payments: [payment],
      });

      // Refrescar el indicador de efectivo en caja del header.
      queryClient.invalidateQueries({ queryKey: cashSessionKeys.current() });

      clearCart();
      onClose();
      navigate(`/sale/${issued.id}/success`, { replace: true });
    } catch (e) {
      // 401 ya disparó el redirect global a /login en setup-api; mostrar un
      // error rojo acá flashea por unos ms antes del reload — mejor saltarlo.
      if (e instanceof ApiError && e.status === 401) return;
      setSubmissionError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setIsEmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="flex max-h-full w-full max-w-lg flex-col rounded-[var(--radius-card)] bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-brand-50 p-2 text-brand-700">
              <CreditCard className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900">Cobrar venta</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isEmitting}
            aria-label="Cerrar"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="flex items-baseline justify-between rounded-2xl bg-slate-900 px-5 py-4 text-white">
            <span className="text-sm font-semibold text-slate-300">Total a cobrar</span>
            <span className="text-3xl font-extrabold tabular-nums">{formatPEN(totals.total)}</span>
          </div>

          <SeriesPicker series={series.data} value={seriesId} onChange={setSeriesId} />

          {customerMode !== "hidden" && (
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                Cliente
                {customerMode === "required-ruc" && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                    RUC requerido
                  </span>
                )}
                {customerMode === "optional" && (
                  <span className="text-xs font-normal text-slate-500">(opcional)</span>
                )}
              </label>
              <CustomerSelector
                value={customer}
                onChange={setCustomer}
                requiredDocType={customerMode === "required-ruc" ? CustomerDocType.RUC : undefined}
                onCreateNew={(seedQuery) => {
                  setCustomerFormSeed(seedQuery);
                  setShowCustomerForm(true);
                }}
              />
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Método de pago</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(METHOD_LABELS) as PaymentMethod[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={
                    "rounded-xl border px-3 py-2.5 text-sm font-bold transition " +
                    (method === m
                      ? "border-brand-600 bg-brand-600 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300")
                  }
                >
                  {METHOD_LABELS[m]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Monto recibido (S/)
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-2xl font-bold tabular-nums outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15"
            />
          </div>

          {method !== PaymentMethod.CASH && (
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Referencia / N° de operación
              </label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-brand-600"
                placeholder="opcional"
              />
            </div>
          )}

          {method === PaymentMethod.CASH && change > 0 && (
            <div className="flex items-center justify-between rounded-xl bg-brand-50 px-4 py-3">
              <span className="text-sm font-semibold text-brand-900">Vuelto</span>
              <span className="text-xl font-extrabold tabular-nums text-brand-700">
                {formatPEN(change)}
              </span>
            </div>
          )}
          {remaining > 0 && (
            <div className="flex items-center justify-between rounded-xl bg-amber-50 px-4 py-3">
              <span className="text-sm font-medium text-amber-900">Falta</span>
              <span className="text-xl font-bold tabular-nums text-amber-700">
                {formatPEN(remaining)}
              </span>
            </div>
          )}

          {submissionError && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {submissionError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isEmitting}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleEmit}
            disabled={!canEmit}
            className="flex items-center gap-2 rounded-2xl bg-brand-600 px-8 py-3 text-base font-extrabold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {isEmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEmitting ? "Emitiendo..." : "Emitir"}
          </button>
        </div>
      </div>

      {/* Form de nuevo cliente. Se monta encima del PaymentDialog (mismo z, último gana). */}
      <CustomerFormDialog
        open={showCustomerForm}
        customer={null}
        initialDocNumber={customerFormSeed}
        onClose={() => setShowCustomerForm(false)}
        onCreated={(created) => setCustomer(created)}
      />
    </div>
  );
}

interface SeriesPickerProps {
  series: DocumentSeriesOut[] | undefined;
  value: string | null;
  onChange: (id: string) => void;
}

function SeriesPicker({ series, value, onChange }: SeriesPickerProps) {
  if (!series) return null;
  if (series.length === 0) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        No hay series activas. Pide al administrador que cree una en Series.
      </p>
    );
  }
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-slate-700">Serie</label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-brand-600"
      >
        {series.map((s) => (
          <option key={s.id} value={s.id}>
            {s.series_code} · {s.document_type} · próximo N° {s.next_number}
          </option>
        ))}
      </select>
    </div>
  );
}
